import { AlertProps, Box } from "@mui/material";
import { AdminResponse } from "entities/auth/api/getAdmins";
import useDeleteAdminMutation from "entities/auth/mutations/useDeleteAdminMutation";
import useResetPasswordMutation from "entities/auth/mutations/useResetPasswordMutation";
import useAdminListQuery from "entities/auth/queries/useAdminListQuery";
import { FC, PropsWithChildren, ReactNode, useState } from "react";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { useModalState } from "shared/hooks/useModalState";
import { createContext } from "shared/utils/context";
import { StyledDescription, StyledTitle } from "../styled";
import { RolesAndPermissionsPage } from "./types";

const [Provider, useRolesAndPermissionsPage] =
  createContext<RolesAndPermissionsPage>({
    name: "RolesAndPermissionsPageContext",
    hookName: "useRolesAndPermissionsPage",
    providerName: "RolesAndPermissionsPageProvider",
  });

const RolesAndPermissionsPageProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const adminDetailsDrawer = useDrawerState<string>();
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>();
  const [alertSeverity, setAlertSeverity] = useState<AlertProps["severity"]>();
  const [alertMessage, setAlertMessage] = useState<ReactNode>();

  const [adminToDelete, setAdminToDelete] = useState<AdminResponse | null>(
    null,
  );
  const deleteAdminModal = useModalState();
  const resetPasswordModal = useModalState();

  const { data = [], isLoading } = useAdminListQuery();

  const { mutate: deleteAdmin, isPending: isPendingDeleteAdmin } =
    useDeleteAdminMutation(deleteAdminModal.closeModal);

  const handleDeleteAdmin = () => {
    if (!deleteAdminModal.modalDetails?.id) {
      return;
    }

    deleteAdmin({ id: deleteAdminModal.modalDetails.id });
  };

  const { mutate: resetPassword, isPending: isPendingResetPassword } =
    useResetPasswordMutation(() => {
      resetPasswordModal.closeModal();
      setAlertSeverity("success");
      setAlertMessage(
        <Box>
          <StyledTitle>Success!</StyledTitle>
          <StyledDescription>
            Password recovery email sent successfully.
          </StyledDescription>
        </Box>,
      );
      setIsAlertOpen(true);
    });

  const handleResetPassword = () => {
    if (!resetPasswordModal.modalDetails?.email) {
      return;
    }

    resetPassword(
      { email: resetPasswordModal.modalDetails.email },
      {
        onError: (error) => {
          resetPasswordModal.closeModal();
          setAlertSeverity("error");
          setAlertMessage(
            <Box>
              <StyledTitle>Error!</StyledTitle>
              <StyledDescription>
                An error occurred while sending the password recovery email.
              </StyledDescription>
            </Box>,
          );
          setIsAlertOpen(true);
        },
      },
    );
  };

  const value: RolesAndPermissionsPage = {
    adminList: data,
    isLoading,
    adminDetailsDrawer,
    isPendingDeleteAdmin,
    adminToDelete,
    deleteAdminModal,
    handleDeleteAdmin,
    setAdminToDelete,
    resetPasswordModal,
    handleResetPassword,
    isPendingResetPassword,
    isAlertOpen,
    setIsAlertOpen,
    alertSeverity,
    alertMessage,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { RolesAndPermissionsPageProvider, useRolesAndPermissionsPage };
